import gql from 'graphql-tag';

import { PhotoThumbnails } from './album';
import { AlbumFolderDetails } from './album-folder';
import {
  AlbumFolderRosterIdentityBaseDetails,
  RosterIdentityBaseDetails,
} from './roster-identity';
import { IdentityYearbookPhotoDetails } from './yearbook-photos';
import { UniformAlbumFolderTagLevelTeam } from './album-folder-tags';

export const AccountIdentityName = gql`
  fragment AccountIdentityName on Identity {
    account_identity_name {
      id
      name
      last_name
    }
  }
`;

export const IdentityDetailsBase = gql`
  fragment IdentityDetailsBase on Identity {
    id
    relationship
    name
    last_name
    has_outdated_selfie
    gender
    current_photo {
      ...PhotoThumbnails
    }
    current_matching_photo {
      ...PhotoThumbnails
    }
    ...AccountIdentityName
  }
  ${PhotoThumbnails}
  ${AccountIdentityName}
`;

export const IdentityDetails = gql`
  fragment IdentityDetails on Identity {
    id
    ...IdentityDetailsBase
  }
  ${IdentityDetailsBase}
`;

export const IdentityJoinedAlbums = gql`
  fragment IdentityJoinedAlbums on Identity {
    id
    matched_photos_extended {
      total
    }
    entitled_matched_photos {
      total
    }
    album_memberships(claimed_status_filter: CLAIMED) {
      records {
        matched_photos_extended {
          total
        }
        album {
          id
          root_album_folder {
            id
          }
          price_whole_album {
            value
            currency
          }
        }
        purchased_identities(slice: { limit: 25 }) {
          total
          records {
            id
          }
        }
      }
    }
  }
`;

export const IdentityDetailsWithPurchasedAlbumFolders = gql`
  fragment IdentityDetailsWithPurchasedAlbumFolders on Identity {
    id
    relationship
    name
    current_photo {
      ...PhotoThumbnails
    }
    purchased_album_folders {
      records {
        ...AlbumFolderDetails
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumFolderDetails}
`;

export const IdentityAlbumFolderTagBaseDetails = gql`
  fragment IdentityAlbumFolderTagBaseDetails on IIdentityAlbumFolderTag {
    album_folder_tag {
      ... on IRecord {
        id
      }
      ... on IAlbumFolderTag {
        type
      }
      ... on UniformAlbumFolderTag {
        id
        uniform_numbered
      }
      ... on GradeAlbumFolderTag {
        id
        name
      }
    }
  }
`;

export const IdentityAlbumFolderUniformTagDetails = gql`
  fragment IdentityAlbumFolderUniformTagDetails on IdentityUniformAlbumFolderTag {
    uniform_album_folder_tag_level {
      id
      name
    }
    uniform_album_folder_tag_level_team {
      ...UniformAlbumFolderTagLevelTeam
      jerseys(slice: { limit: 999 }) {
        total
        records {
          id
          name
          colors
        }
      }
    }
    jersey_number
  }
  ${UniformAlbumFolderTagLevelTeam}
`;

export const IdentityHasRosterIdentityDetails = gql`
  fragment IdentityHasRosterIdentityDetails on Identity {
    id
    roster_identity {
      id
      grade
      organization_ref_id
    }
  }
`;

export const IdentityRosterIdentityDetails = gql`
  fragment IdentityRosterIdentityDetails on Identity {
    id
    roster_identity {
      ...RosterIdentityBaseDetails
    }
  }
  ${RosterIdentityBaseDetails}
`;

export const AlbumFolderIdentityDetails = gql`
  fragment AlbumFolderIdentityDetails on Identity {
    id
    name
    last_name
    school_grade
    relationship
    has_outdated_selfie
    matched_photo_notifications(
      album_folder_id: $id
      slice: { limit: 1 }
      without_invitees: true
    ) {
      total
      records {
        created_at
      }
    }
    matched_photos_extended(album_folder_id: $id, without_invitees: true) {
      total
    }
    consumer_maybe_votes(hierarchy_item_id: $id) {
      total
    }
    plic_uploaded_photos: matched_photos_extended(
      album_folder_id: $id
      without_invitees: true
      plic_uploaded_only: true
    ) @include(if: $includePlicUploadedPhotos) {
      total
    }
    current_matching_photo {
      id
      is_owner
      ...PhotoThumbnails
    }
    current_photo {
      id
      is_owner
      ...PhotoThumbnails
    }
    face_highlighted_photos(album_folder_id: $id) {
      total
    }
    account {
      id
      name
      type
      phone_number
      email_address
      email_verified
      current_selfie {
        ...PhotoThumbnails
      }
      transaction_items(
        album_folder_ids: [$id]
        include_album_folder_albums: true
        exclude_prepaid_configurations: $excludePrepaidConfigurations
      ) {
        total
      }
      broadcast_recipients_count(hierarchy_item_id: $id)
      last_broadcast_recipient(hierarchy_item_id: $id) {
        id
        created_at
      }
    }
    ...AccountIdentityName
    purchased_album_folders(slice: { limit: 9999 }) {
      records {
        id
      }
    }
    album_folder_tags(album_folder_id: $id) {
      records {
        id
        ...IdentityAlbumFolderTagBaseDetails
        ...IdentityAlbumFolderUniformTagDetails
      }
    }
    roster_identity {
      id
      ...AlbumFolderRosterIdentityBaseDetails
      purchased_custom_prints {
        total
      }
    }
    ...IdentityYearbookPhotoDetails
  }
  ${PhotoThumbnails}
  ${AccountIdentityName}
  ${IdentityAlbumFolderTagBaseDetails}
  ${IdentityAlbumFolderUniformTagDetails}
  ${AlbumFolderRosterIdentityBaseDetails}
  ${IdentityYearbookPhotoDetails}
`;
